import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  BaseEdge,
  Edge,
  EdgeLabelRenderer,
  EdgeProps,
  useReactFlow,
  useStore,
} from 'reactflow';
import useFlowsStore from '../../store/storeFlows';
import { getEdgePath, getWaypointPath, Waypoint } from './utils';
import { FlowService } from '../../service/FlowService';
import EdgeDeleteButton from './EdgeDeleteButton';

const selectSelectedEdgesCount = (s: { edges: Edge[] }) =>
  s.edges.filter(e => e.selected).length;

function getPathEndpoints(d: string): {
  pathStart: DOMPoint;
  pathEnd: DOMPoint;
} {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', d);
  svg.appendChild(path);
  svg.style.position = 'fixed';
  svg.style.opacity = '0';
  svg.style.pointerEvents = 'none';
  document.body.appendChild(svg);
  const total = path.getTotalLength();
  const start = path.getPointAtLength(0);
  const end = path.getPointAtLength(total);
  document.body.removeChild(svg);
  return { pathStart: start, pathEnd: end };
}

interface EdgeBaseWrapperProps extends EdgeProps {
  children?: React.ReactNode;
  renderLabel?: (coords: { x: number; y: number }) => React.ReactNode;
  showDeleteButton?: boolean;
}

const EdgeBaseWrapper = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
  style,
  markerEnd,
  markerStart,
  interactionWidth = 20,
  children,
  renderLabel,
  showDeleteButton = true,
}: EdgeBaseWrapperProps) => {
  const lineType = useFlowsStore(state => state.lineType);
  const storeWaypoints = useFlowsStore(state => state.waypointsMap[id]);
  const setWaypoints = useFlowsStore(state => state.setWaypoints);
  const { setEdges, getEdges } = useReactFlow();
  const transform = useStore(s => s.transform);
  const selectedEdgesCount = useStore(selectSelectedEdgesCount);

  const adjustedTargetY = targetY - 5;

  const showController =
    selected && showDeleteButton && selectedEdgesCount <= 1;

  const waypoints: Waypoint[] = storeWaypoints ?? data?.waypoints ?? [];
  const hasWaypoints = waypoints.length > 0;

  const waypointsRef = useRef(waypoints);
  waypointsRef.current = waypoints;

  const transformRef = useRef(transform);
  transformRef.current = transform;

  const prevSelectedRef = useRef(selected);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveEdges = useCallback(() => {
    const flowId = useFlowsStore.getState().flowId;
    const allEdges = getEdges().map(({ selected: _s, ...edge }) => ({
      ...edge,
      data: {
        ...edge.data,
        waypoints: edge.id === id ? waypointsRef.current : edge.data?.waypoints,
      },
    }));
    FlowService.updateFlow(flowId, { edges: allEdges });
  }, [getEdges, id]);

  const debouncedSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveEdges();
      saveTimerRef.current = null;
    }, 300);
  }, [saveEdges]);

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (prevSelectedRef.current && !selected && hasWaypoints) {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      saveEdges();
    }
    prevSelectedRef.current = selected;
  }, [selected, hasWaypoints, saveEdges]);

  const updateWaypointsVisual = useCallback(
    (next: Waypoint[]) => {
      setWaypoints(id, next);
      waypointsRef.current = next;
    },
    [id, setWaypoints],
  );

  const syncEdgeData = useCallback(() => {
    setEdges(eds =>
      eds.map(e =>
        e.id === id
          ? { ...e, data: { ...e.data, waypoints: waypointsRef.current } }
          : e,
      ),
    );
  }, [id, setEdges]);

  const screenToFlow = useCallback((clientX: number, clientY: number) => {
    const [tx, ty, zoom] = transformRef.current;
    return { x: (clientX - tx) / zoom, y: (clientY - ty) / zoom };
  }, []);

  const handleAddWaypoint = useCallback(
    (segmentIdx: number) => {
      const allPoints = [
        { x: sourceX, y: sourceY },
        ...waypointsRef.current,
        { x: targetX, y: adjustedTargetY },
      ];
      const a = allPoints[segmentIdx];
      const b = allPoints[segmentIdx + 1];
      const newWp: Waypoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const next = [...waypointsRef.current];
      next.splice(segmentIdx, 0, newWp);
      updateWaypointsVisual(next);
      syncEdgeData();
      debouncedSave();
    },
    [
      sourceX,
      sourceY,
      targetX,
      adjustedTargetY,
      updateWaypointsVisual,
      syncEdgeData,
      debouncedSave,
    ],
  );

  const handleWaypointDrag = useCallback(
    (idx: number, e: React.MouseEvent) => {
      const startFlow = screenToFlow(e.clientX, e.clientY);
      const { x: startWpX, y: startWpY } = waypointsRef.current[idx];

      const onMove = (ev: MouseEvent) => {
        const curr = screenToFlow(ev.clientX, ev.clientY);
        const next = [...waypointsRef.current];
        next[idx] = {
          x: startWpX + (curr.x - startFlow.x),
          y: startWpY + (curr.y - startFlow.y),
        };
        updateWaypointsVisual(next);
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        syncEdgeData();
        debouncedSave();
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [screenToFlow, updateWaypointsVisual, syncEdgeData, debouncedSave],
  );

  const handleWaypointDelete = useCallback(
    (idx: number, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const next = waypointsRef.current.filter((_, i) => i !== idx);
      updateWaypointsVisual(next);
      syncEdgeData();
      debouncedSave();
    },
    [updateWaypointsVisual, syncEdgeData, debouncedSave],
  );

  const { edgePath, labelX, labelY, segmentMidpoints } = useMemo(() => {
    if (hasWaypoints) {
      const [p, lx, ly, mids] = getWaypointPath(
        sourceX,
        sourceY,
        targetX,
        adjustedTargetY,
        waypoints,
        lineType,
      );
      return { edgePath: p, labelX: lx, labelY: ly, segmentMidpoints: mids };
    }

    const [p, lx, ly] = getEdgePath(lineType, {
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY: adjustedTargetY,
      targetPosition,
    });

    const mids: Waypoint[] = selected
      ? [{ x: (sourceX + targetX) / 2, y: (sourceY + adjustedTargetY) / 2 }]
      : [];

    return { edgePath: p, labelX: lx, labelY: ly, segmentMidpoints: mids };
  }, [
    hasWaypoints,
    sourceX,
    sourceY,
    targetX,
    adjustedTargetY,
    waypoints,
    lineType,
    sourcePosition,
    targetPosition,
    selected,
  ]);

  const { pathStart, pathEnd } = useMemo(
    () => getPathEndpoints(edgePath),
    [edgePath],
  );

  return (
    <>
      {children}

      <BaseEdge
        id={id}
        path={edgePath}
        interactionWidth={selected ? 0 : interactionWidth}
        markerEnd={markerEnd}
        markerStart={markerStart}
        style={style}
      />

      <EdgeLabelRenderer>
        {showController && (
          <>
            <EdgeDeleteButton
              id={id}
              labelX={pathStart.x}
              labelY={pathStart.y}
            />
            <EdgeDeleteButton id={id} labelX={pathEnd.x} labelY={pathEnd.y} />
          </>
        )}

        {selected &&
          waypoints.map((wp, idx) => (
            <WaypointHandle
              key={`wp-${id}-${idx}`}
              wp={wp}
              idx={idx}
              onDrag={handleWaypointDrag}
              onDelete={handleWaypointDelete}
            />
          ))}

        {selected &&
          segmentMidpoints.map((mp, idx) => (
            <AddWaypointHandle
              key={`add-${id}-${idx}`}
              mp={mp}
              idx={idx}
              onAdd={handleAddWaypoint}
            />
          ))}

        {renderLabel?.({ x: labelX, y: labelY })}
      </EdgeLabelRenderer>
    </>
  );
};

// ─── Sub-components (stable, avoid inline object/fn allocation in lists) ─────

interface WaypointHandleProps {
  wp: Waypoint;
  idx: number;
  onDrag: (idx: number, e: React.MouseEvent) => void;
  onDelete: (idx: number, e: React.MouseEvent) => void;
}

const waypointStyle = (x: number, y: number): React.CSSProperties => ({
  position: 'absolute',
  transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: '#3650DE',
  border: '2px solid white',
  boxShadow: '0 0 4px rgba(0,0,0,0.3)',
  cursor: 'grab',
  pointerEvents: 'all',
  zIndex: 1001,
});

const WaypointHandle = memo(
  ({ wp, idx, onDrag, onDelete }: WaypointHandleProps) => (
    <div
      style={waypointStyle(wp.x, wp.y)}
      onMouseDown={e => {
        e.stopPropagation();
        e.preventDefault();
        onDrag(idx, e);
      }}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onContextMenu={e => onDelete(idx, e)}
    />
  ),
);

interface AddWaypointHandleProps {
  mp: Waypoint;
  idx: number;
  onAdd: (idx: number) => void;
}

const addHandleStyle = (x: number, y: number): React.CSSProperties => ({
  position: 'absolute',
  transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: 'white',
  border: '2px solid #3650DE',
  color: '#3650DE',
  fontSize: 18,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  pointerEvents: 'all',
  zIndex: 1001,
  lineHeight: 1,
});

const AddWaypointHandle = memo(({ mp, idx, onAdd }: AddWaypointHandleProps) => (
  <div
    style={addHandleStyle(mp.x, mp.y)}
    onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }}
    onClick={e => {
      e.stopPropagation();
      e.preventDefault();
      onAdd(idx);
    }}
  />
));

export default memo(EdgeBaseWrapper);
